import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery
} from '@mui/material';
import {
  AddRounded,
  DeleteOutlineRounded,
  EditRounded,
  OndemandVideoRounded,
  PlayCircleOutlineRounded,
  TrendingUpRounded
} from '@mui/icons-material';

const getYouTubeThumbnail = (videoId) => `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
const getYouTubeLink = (videoId) => `https://www.youtube.com/embed/${videoId}?&controls=1&mute=0&disablekb=1`;

const ManagePartners = () => {
  const [partners, setPartners] = useState([]);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:900px)');

  useEffect(() => {
    axios.get('https://aaryanholidays.in/backend/partners')
      .then(response => setPartners(response.data))
      .catch(error => console.error('Error fetching partners:', error));
  }, []);

  const stats = useMemo(() => {
    return {
      total: partners.length,
      promoted: partners.filter((partner) => Number(partner.promotion_order) > 0).length
    };
  }, [partners]);

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this partner?')) {
      axios.delete(`https://aaryanholidays.in/backend/partners/${id}`)
        .then(() => setPartners(partners.filter(partner => partner.id !== id)))
        .catch(error => console.error('Error deleting partner:', error));
    }
  };

  const statCards = [
    {
      label: 'Total Partners',
      value: stats.total,
      icon: OndemandVideoRounded,
      tone: 'linear-gradient(135deg, #0f766e 0%, #2dd4bf 100%)'
    },
    {
      label: 'Promoted Entries',
      value: stats.promoted,
      icon: TrendingUpRounded,
      tone: 'linear-gradient(135deg, #1d4ed8 0%, #60a5fa 100%)'
    }
  ];

  return (
    <Box>
      <Card
        sx={{
          mb: 3,
          borderRadius: 5,
          border: '1px solid rgba(148, 163, 184, 0.16)',
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)'
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.75 }}>
                Manage Partners
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b', maxWidth: 760 }}>
                Present premium partner stories with cleaner visuals, stronger ordering, and fast access to edits.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              component={Link}
              to="/add-partner"
              sx={{
                borderRadius: 999,
                px: 2.5,
                py: 1.2,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: '0 18px 36px rgba(37, 99, 235, 0.24)'
              }}
            >
              Add New Partner
            </Button>
          </Stack>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {statCards.map((item) => {
              const Icon = item.icon;

              return (
                <Grid item xs={12} sm={6} key={item.label}>
                  <Box
                    sx={{
                      p: 2.25,
                      borderRadius: 4,
                      border: '1px solid rgba(148, 163, 184, 0.16)',
                      backgroundColor: '#f8fafc'
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 3,
                          display: 'grid',
                          placeItems: 'center',
                          color: '#ffffff',
                          background: item.tone
                        }}
                      >
                        <Icon />
                      </Box>
                      <Box>
                        <Typography variant="overline" sx={{ color: '#64748b', letterSpacing: '0.12em' }}>
                          {item.label}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                          {item.value}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {isMobile ? (
        <Grid container spacing={2}>
          {partners.map((partner) => (
            <Grid item xs={12} key={partner.id}>
              <Card
                sx={{
                  borderRadius: 4,
                  border: '1px solid rgba(148, 163, 184, 0.16)',
                  boxShadow: '0 18px 40px rgba(15, 23, 42, 0.06)'
                }}
              >
                <CardContent sx={{ p: 2.25 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        variant="rounded"
                        src={getYouTubeThumbnail(partner.video_link)}
                        alt={partner.name}
                        sx={{ width: 72, height: 54, borderRadius: 2 }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {partner.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={`Order ${partner.promotion_order}`}
                          sx={{
                            mt: 0.75,
                            borderRadius: 999,
                            backgroundColor: alpha('#2563eb', 0.08),
                            color: '#1d4ed8',
                            fontWeight: 700
                          }}
                        />
                      </Box>
                    </Stack>

                    <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.7 }}>
                      {partner.brief}
                    </Typography>

                    <Stack direction="row" spacing={1}>
                      <Button
                        component="a"
                        href={getYouTubeLink(partner.video_link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<PlayCircleOutlineRounded />}
                        variant="outlined"
                        sx={{
                          borderRadius: 999,
                          textTransform: 'none',
                          fontWeight: 700
                        }}
                      >
                        Preview
                      </Button>
                      <Button
                        onClick={() => navigate(`/edit-partner/${partner.id}`)}
                        startIcon={<EditRounded />}
                        variant="outlined"
                        sx={{
                          borderRadius: 999,
                          textTransform: 'none',
                          fontWeight: 700
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDelete(partner.id)}
                        startIcon={<DeleteOutlineRounded />}
                        color="error"
                        variant="outlined"
                        sx={{
                          borderRadius: 999,
                          textTransform: 'none',
                          fontWeight: 700
                        }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 5,
            overflow: 'hidden',
            border: '1px solid rgba(148, 163, 184, 0.16)',
            boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 800, color: '#334155' }}>Partner</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#334155' }}>Brief</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#334155' }}>Promotion Order</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#334155' }}>Preview</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#334155' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {partners.map((partner) => (
                <TableRow
                  key={partner.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: alpha('#2563eb', 0.03)
                    }
                  }}
                >
                  <TableCell sx={{ minWidth: 220 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        variant="rounded"
                        src={getYouTubeThumbnail(partner.video_link)}
                        alt={partner.name}
                        sx={{ width: 72, height: 54, borderRadius: 2.5 }}
                      />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {partner.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          Video-based partner spotlight
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: '#475569', lineHeight: 1.7, maxWidth: 520 }}>
                    {partner.brief}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={partner.promotion_order}
                      sx={{
                        borderRadius: 999,
                        backgroundColor: alpha('#2563eb', 0.08),
                        color: '#1d4ed8',
                        fontWeight: 700
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      component="a"
                      href={getYouTubeLink(partner.video_link)}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<PlayCircleOutlineRounded />}
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        textTransform: 'none',
                        fontWeight: 700
                      }}
                    >
                      Watch
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        onClick={() => navigate(`/edit-partner/${partner.id}`)}
                        sx={{
                          borderRadius: 2.5,
                          backgroundColor: alpha('#2563eb', 0.08),
                          color: '#2563eb',
                          '&:hover': {
                            backgroundColor: alpha('#2563eb', 0.14)
                          }
                        }}
                      >
                        <EditRounded fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(partner.id)}
                        sx={{
                          borderRadius: 2.5,
                          backgroundColor: alpha('#dc2626', 0.08),
                          color: '#dc2626',
                          '&:hover': {
                            backgroundColor: alpha('#dc2626', 0.14)
                          }
                        }}
                      >
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ManagePartners;
